import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { XCircle, Ban, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const when = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');
const money = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

// Configurações → Fiscal: cancelamento de NFC-e autorizada e inutilização de numeração (testes de homologação SEFAZ-MS)
export const FiscalHomologationTools = ({ ambiente = 'homologacao' }) => (
  <div className="space-y-4" data-testid="fiscal-homolog-tools">
    <div className={`rounded-md border p-3 text-xs ${ambiente === 'producao' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="fiscal-tools-env">
      <b>Ambiente atual: {ambiente === 'producao' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}.</b> As operações abaixo são transmitidas à SEFAZ via Focus NFe{ambiente === 'producao' ? ' e têm efeito fiscal real.' : ' no ambiente de testes (sem valor fiscal) — use-as para cumprir os testes obrigatórios de cancelamento e inutilização.'}
    </div>
    <CancelNfceCard />
    <InutilizacaoCard />
  </div>
);

const CancelNfceCard = () => {
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [just, setJust] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await axios.get(`${API}/nfce/authorized?limit=50`, cfg); setRows(r.data); } catch (_e) { setRows([]); } };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/nfce/cancel/${target.sale_id}`, { data: { justificativa: just.trim() }, ...cfg });
      toast.success(`NFC-e nº ${target.number} cancelada${r.data.cancel_status_sefaz ? ` · SEFAZ ${r.data.cancel_status_sefaz}: ${r.data.cancel_mensagem || ''}` : ''}`);
      setTarget(null); setJust(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Falha ao cancelar'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="nfce-cancel-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><XCircle size={16} className="text-red-600" /> Cancelar NFC-e autorizada</h3>
        <Button type="button" size="sm" variant="outline" onClick={load} className="h-8" data-testid="nfce-cancel-refresh"><RefreshCw size={14} className="mr-1" /> Atualizar</Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Selecione a NFC-e e informe a justificativa (mínimo 15 caracteres). O cancelamento é transmitido à SEFAZ pela Focus NFe e registrado na venda.</p>
      {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid="nfce-cancel-empty">Nenhuma NFC-e autorizada encontrada.</p> : (
        <div className="max-h-72 overflow-y-auto rounded border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600 sticky top-0"><tr><th className="text-left px-2 py-1">NFC-e</th><th className="text-left">Venda</th><th className="text-left">Emitida</th><th className="text-right">Valor</th><th className="text-left px-2">Amb.</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.sale_id} className="border-t border-slate-100" data-testid={`nfce-auth-row-${r.sale_id}`}>
                <td className="px-2 py-1 font-mono">nº {r.number} / s{r.serie}</td>
                <td>#{r.sale_number}</td>
                <td>{when(r.issued_at || r.created_at)}</td>
                <td className="text-right font-mono">{money(r.total)}</td>
                <td className="px-2">{r.ambiente === 'producao' ? 'PROD' : 'HOMOL'}</td>
                <td className="text-right pr-2"><button type="button" onClick={() => { setTarget(r); setJust(''); }} className="text-red-600 hover:text-red-700 font-semibold" data-testid={`nfce-cancel-btn-${r.sale_id}`}>Cancelar</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {target && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" data-testid="nfce-cancel-tool-modal">
          <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
            <h3 className="text-lg font-bold text-slate-900">Cancelar NFC-e nº {target.number} (venda #{target.sale_number})</h3>
            <p className="text-xs text-slate-600">Chave: <span className="font-mono break-all">{target.access_key || '—'}</span></p>
            <div className="space-y-1">
              <Label className="text-xs">Justificativa * <span className="text-slate-400">({just.trim().length}/15 mín.)</span></Label>
              <textarea value={just} onChange={(e) => setJust(e.target.value)} rows={3} maxLength={255} className="w-full rounded-md border border-slate-300 p-2 text-sm" placeholder="Ex.: Teste de cancelamento para homologação SEFAZ-MS" data-testid="nfce-cancel-tool-just" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setTarget(null)} disabled={busy} data-testid="nfce-cancel-tool-close">Voltar</Button>
              <Button type="button" onClick={cancel} disabled={busy || just.trim().length < 15} className="bg-red-600 hover:bg-red-700" data-testid="nfce-cancel-tool-confirm">{busy ? 'Transmitindo…' : 'Confirmar cancelamento'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InutilizacaoCard = () => {
  const [f, setF] = useState({ serie: '1', numero_inicial: '', numero_final: '', justificativa: '' });
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await axios.get(`${API}/nfce/inutilizacoes`, cfg); setHist(r.data); } catch (_e) { setHist([]); } };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = Number(f.serie) >= 1 && Number(f.numero_inicial) >= 1 && Number(f.numero_final) >= Number(f.numero_inicial) && f.justificativa.trim().length >= 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Inutilizar série ${f.serie}, números ${f.numero_inicial} a ${f.numero_final} na SEFAZ?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/nfce/inutilizacao`, { serie: Number(f.serie), numero_inicial: Number(f.numero_inicial), numero_final: Number(f.numero_final), justificativa: f.justificativa.trim() }, cfg);
      toast.success(`Inutilização ${r.data.status || 'enviada'} · SEFAZ ${r.data.status_sefaz || ''} ${r.data.mensagem_sefaz || ''}`);
      setF({ ...f, numero_inicial: '', numero_final: '', justificativa: '' }); load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Falha na inutilização'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="nfce-inut-card">
      <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-1"><Ban size={16} className="text-slate-700" /> Inutilizar numeração de NFC-e</h3>
      <p className="text-xs text-slate-500 mb-3">Para números pulados/não utilizados (ex.: falha antes da autorização). A faixa não pode conter números já autorizados. Justificativa mínima de 15 caracteres.</p>
      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div className="space-y-1"><Label className="text-xs">Série</Label><Input type="number" min="1" value={f.serie} onChange={set('serie')} data-testid="inut-serie" /></div>
        <div className="space-y-1"><Label className="text-xs">Nº inicial</Label><Input type="number" min="1" value={f.numero_inicial} onChange={set('numero_inicial')} data-testid="inut-ini" /></div>
        <div className="space-y-1"><Label className="text-xs">Nº final</Label><Input type="number" min="1" value={f.numero_final} onChange={set('numero_final')} data-testid="inut-fim" /></div>
        <div className="space-y-1 col-span-2 md:col-span-3"><Label className="text-xs">Justificativa * <span className="text-slate-400">({f.justificativa.trim().length}/15 mín.)</span></Label><Input value={f.justificativa} onChange={set('justificativa')} maxLength={255} placeholder="Ex.: Numeração pulada por falha de transmissão" data-testid="inut-just" /></div>
        <div className="col-span-2 md:col-span-6 flex justify-end">
          <Button type="submit" disabled={!valid || busy} className="bg-slate-900 hover:bg-black" data-testid="inut-submit"><Ban size={14} className="mr-1" /> {busy ? 'Transmitindo…' : 'Inutilizar na SEFAZ'}</Button>
        </div>
      </form>
      {hist.length > 0 && (
        <div className="mt-3 rounded border border-slate-200 overflow-hidden" data-testid="inut-history">
          <div className="bg-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-600">Inutilizações realizadas</div>
          <table className="w-full text-xs">
            <thead className="text-slate-500"><tr><th className="text-left px-3 py-1">Quando</th><th className="text-left">Série / Faixa</th><th className="text-left">Amb.</th><th className="text-left">Resultado</th><th className="text-left">Justificativa</th></tr></thead>
            <tbody>{hist.map((h, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`inut-row-${i}`}>
                <td className="px-3 py-1">{when(h.created_at)}</td>
                <td className="font-mono">s{h.serie}: {h.numero_inicial}–{h.numero_final}</td>
                <td>{h.ambiente === 'producao' ? 'PROD' : 'HOMOL'}</td>
                <td className={h.status === 'autorizado' ? 'text-emerald-700 font-bold' : 'text-red-600 font-bold'}>{h.status || '—'} {h.status_sefaz ? `(${h.status_sefaz})` : ''} {h.mensagem_sefaz || ''}</td>
                <td className="truncate max-w-[220px]" title={h.justificativa}>{h.justificativa}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
